import { createUniqueId, deepEquals, delay, exists } from '@jack-henry/frontend-utils/functions';
import { ObservationSource } from '@jack-henry/frontend-utils/observable';
import { Action } from '@jack-henry/frontend-utils/types';
import { debounce, isEmptyString } from '@treasury/utils';
import { property, state } from 'lit/decorators.js';
import { TmBaseComponent } from '../../../tm-base.component';
import {
    FORM_CONTROL_CLASS_NAME,
    FormControlValidationResult,
    FormControlEventPayload as Payload,
    TmFormControlEvent,
    dispatchFormControlEvent,
} from './tm-form-control.types';

export abstract class TmFormControl<T> extends TmBaseComponent {
    /**
     * The initial value used to hydrate the control from a template.
     *
     * **Note:** This affects—but is distinct from—the `value` property.
     * Do not use this to set the current value of the control;
     * call `changeValue()` instead.
     */
    @property({ attribute: false, reflect: false })
    public initial?: T;

    @property({
        type: String,
    })
    public name = `${this.tagName.toLowerCase()}-${createUniqueId()}`;

    @property({ type: Boolean })
    public required = false;

    /**
     * Externally-set validity state.
     * Primarily exists for consumers to manually set the state of a `TmFormControl`.
     *
     * Influences but is not the same as `isValid`.
     *
     * TODO: consolidate `invalid` and `isValid` concepts.
     */
    @property({ type: Boolean })
    public invalid = false;

    protected cleanupTasks: Action[] = [];

    protected readonly valueChangedSource = new ObservationSource<
        Payload<T>[TmFormControlEvent.ValueChanged]
    >();

    // eslint-disable-next-line @typescript-eslint/member-ordering
    public readonly valueChanged$ = this.valueChangedSource.toObservable();

    protected readonly validityChangedSource = new ObservationSource<
        FormControlValidationResult<T>
    >();

    // eslint-disable-next-line @typescript-eslint/member-ordering
    public readonly validityChanged$ = this.validityChangedSource.toObservable();

    protected readonly disconnectedSource = new ObservationSource<void>();

    /**
     * Used instead of dispatching an event since the element is already removed
     * from the DOM tree by the time `disconnectedCallback()` lifecycle hook is invoked.
     */
    // eslint-disable-next-line @typescript-eslint/member-ordering
    public readonly disconnected$ = this.disconnectedSource.toObservable();

    /**
     * The last validation result calculated by `performValidation()`.
     *
     * Note that this is subtly different than the `isValid` property.
     * That should be treated as the source of truth for control validity.
     */
    @state()
    private lastValidation?: FormControlValidationResult<T>;

    public get errorMessages() {
        return this.lastValidation instanceof FormControlValidationResult
            ? this.lastValidation.errors
            : undefined;
    }

    @state()
    private _value?: T;

    /**
     * Debounced value change logic delegated to by the public API's `changeValue()` method.
     * Prevents rapid-fire value changes from overwhelming form validation cycles and
     * causing out-of-order asynchronous updates.
     *
     * This is defined as a private field in order to allow child classes to call
     * the public method via `super` and to enable use with `debounce()`.
     */
    private readonly changeValueDebounced = debounce(async (value?: T) => {
        this._value = value;

        await this.performValidation();
        await this.updateComplete;

        const payload: Payload<T>[TmFormControlEvent.ValueChanged] = {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            value: value as any,
            isValid: this.isValid,
        };

        dispatchFormControlEvent(this, TmFormControlEvent.ValueChanged, payload);
        this.valueChangedSource.emit(payload);
    }, 300);

    /**
     * The current, read-only value of the control.
     *
     * Use `changeValue()` if you wish to mutate the value programmatically
     * or set `initial` if you wish to assign a value on first render.
     */
    public get value() {
        return this._value;
    }

    public get isValid() {
        if (this.invalid) {
            return false;
        }

        return this.lastValidation?.isValid ?? true;
    }

    public connectedCallback() {
        super.connectedCallback();
        this.classList.add(FORM_CONTROL_CLASS_NAME);
        dispatchFormControlEvent(this, TmFormControlEvent.ControlConnected, {
            control: this,
        });
    }

    public disconnectedCallback(): void {
        super.disconnectedCallback();
        this.disconnectedSource.emit();
        this.cleanupTasks.forEach(task => task());
        this.cleanupTasks = [];
    }

    public clearValue() {
        return this.changeValue(undefined);
    }

    public reset() {
        return this.changeValue(this.initial);
    }

    /**
     * Mutate the control with a new `value`, kicking off the form validation cycle.
     */
    public async changeValue(value?: T) {
        if (deepEquals(this._value, value)) {
            return;
        }

        await this.changeValueDebounced(value);
    }

    /**
     * Set the validation result for this control from an external source.
     *
     * This is primarily useful for `TmForm` to set form-level validation results on individual controls.
     */
    public setValidationResult(result: FormControlValidationResult<T>) {
        this.lastValidation =
            this.lastValidation instanceof FormControlValidationResult
                ? this.lastValidation.combine(result)
                : result;
    }

    /**
     * Validates that the format of the form control is correct (e.g., numeric input is parsable as a number).
     *
     * @returns `true`, if the input of the form control is in the correct format;
     * `false`, if is not and there's no error message to display.
     *
     * If a list of messages is returned, the control will be considered invalid
     * and will display these errors in the UI.
     */
    protected abstract validate(): boolean | string[];

    protected firstUpdated(changedProps: Map<PropertyKey, unknown>): void {
        super.firstUpdated(changedProps);
        this.handleInitialChange(changedProps);
    }

    protected updated(changedProps: Map<PropertyKey, unknown>): void {
        super.updated(changedProps);
        this.handleInitialChange(changedProps);
    }

    /**
     * Run validation specified logic from child classes.
     */
    // Widening this to `protected` temporarily so controls can check validity outside `form-changed` mechanism.
    // This is currently used for determining the initial validity of a form control before anything has changed.
    // Because of risk of allowing submission of invalid forms it's not safe to default to `true` as we've done elsewhere.
    // TODO: Make this `private` again once we have a better way to handle initial form control validity.
    protected async performValidation() {
        // allow any pending state changes to propagate before validating;
        // this is primarily to accommodate Vaadin controls during initial render after
        // value assignment, which need a reference to an input element with manual validation
        await delay(1);
        const isValidOrErrList = this.validate();
        const isValid = typeof isValidOrErrList === 'boolean' ? isValidOrErrList : true;
        const errList = Array.isArray(isValidOrErrList) ? isValidOrErrList : [];
        const result = new FormControlValidationResult(this, isValid).addErrors(...errList);

        if (!this.validateRequired()) {
            result.addErrors('This field is required.');
        }

        if (this.lastValidation && this.lastValidation.compare(result)) {
            // if validation results are considered equal, no need to update state
            return;
        }

        this.lastValidation = result;
        this.validityChangedSource.emit(result);
    }

    protected listenForValueChange(
        callback: (payload: Payload<T>[TmFormControlEvent.ValueChanged]) => void
    ) {
        const { unsubscribe } = this.valueChangedSource.subscribe(callback);
        this.cleanupTasks.push(unsubscribe);
    }

    private handleInitialChange(changedProps: Map<PropertyKey, unknown>) {
        if (changedProps.has('initial') && !deepEquals(changedProps.get('initial'), this.initial)) {
            this._value = this.initial;
        }
    }

    /**
     * Validates the required state of the form control.
     * @returns `true` if the control is required and it has a value or it is optional.
     */
    private validateRequired() {
        if (!this.required) {
            return true;
        }

        return exists(this.value) && !isEmptyString(this.value);
    }
}
